import { useState } from "react";
import { FileDown, Trash2, AlertTriangle, Loader2, RotateCcw, Copy, Ban, Eraser, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { ResetDialog } from "./ResetDialog";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { BalanceCheck } from "./BalanceCheck";
import { AnalysisProgress } from "./AnalysisProgress";
import { AddTransactionDialog } from "./AddTransactionDialog";
import { ReviewDialog } from "./ReviewDialog";
import { PrintButtons } from "./PrintDialog";
import { MergeReview } from "./MergeReview";
import { api, errMsg } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";

const Meta = ({ label, value, testId }) => (
  <div className="min-w-0">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-num mt-0.5 break-words text-sm text-slate-900" data-testid={testId}>{value || "–"}</div>
  </div>
);

const fmtIban = (v) => (v ? v.replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim() : null);

const DeleteStatement = ({ st, onDeleted, open, setOpen }) => (
  <AlertDialog open={open} onOpenChange={setOpen}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Kontoauszug löschen?</AlertDialogTitle>
        <AlertDialogDescription>Der Auszug und alle zugehörigen Buchungen werden entfernt.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
        <AlertDialogAction
          data-testid="confirm-delete-statement-btn"
          className="bg-red-700 hover:bg-red-800"
          onClick={async () => {
            try { await api.deleteStatement(st.id); toast.success("Kontoauszug gelöscht"); onDeleted(st.id); }
            catch (e) { toast.error(errMsg(e)); }
          }}
        >Löschen</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const Processing = ({ st }) => {
  const p = st.extraction_progress || {};
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 5;
  return (
    <div data-testid="extraction-progress" className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2 font-semibold text-slate-900"><Loader2 className="h-5 w-5 animate-spin" /> Kontoauszug wird gelesen …</div>
      <Progress value={pct} className="mt-4 h-2" />
      <p className="mt-2 text-sm text-slate-600">{p.total ? `${p.done}/${p.total} Seiten verarbeitet` : "PDF wird vorbereitet"} – die KI strukturiert jede Seite einzeln.</p>
    </div>
  );
};

export const StatementHeader = ({ st, onChanged, onDeleted, onAdded, onSelect }) => {
  const [bulk, setBulk] = useState(false);
  const [reset, setReset] = useState(false);
  const [del, setDel] = useState(false);
  const reextract = () => act(() => api.reextract(st.id), "Extraktion neu gestartet");
  const title = st.virtual ? `Laufende Buchungen ${st.statement_year} (Bankverbindung)` : st.statement_number ? `Kontoauszug ${st.statement_number}` : st.file?.original_filename;
  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-4 fade-up" data-testid="statement-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words text-sm text-slate-500">{st.bank || "Bank"} · {st.virtual ? "noch nicht durch Kontoauszug-PDF abgedeckt" : st.file?.original_filename}</div>
          <h1 className="break-words font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="statement-title">{title}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {!st.virtual && (
            <Button variant="outline" asChild className="gap-1.5" data-testid="open-pdf-btn">
              <a href={api.fileUrl(st.id)} target="_blank" rel="noreferrer"><FileDown className="h-4 w-4" /> PDF</a>
            </Button>
          )}
          <div className="hidden flex-wrap gap-2 md:flex">
            {!st.virtual && st.status !== "processing" && (
              <Button variant="outline" className="gap-1.5" data-testid="reextract-btn" onClick={reextract}><RotateCcw className="h-4 w-4" /> Neu einlesen</Button>
            )}
            {st.status === "ready" && (
              <Button variant="ghost" className="gap-1.5 text-amber-800 hover:bg-amber-50" data-testid="reset-statement-btn" onClick={() => setReset(true)}>
                <Eraser className="h-4 w-4" /> Auszug zurücksetzen
              </Button>
            )}
            {!st.virtual && (
              <Button variant="ghost" data-testid="delete-statement-btn" className="gap-1.5 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => setDel(true)}>
                <Trash2 className="h-4 w-4" /> Auszug löschen
              </Button>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-11 gap-1.5 md:hidden" data-testid="statement-more-btn"><MoreHorizontal className="h-4 w-4" /> Mehr</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!st.virtual && st.status !== "processing" && <DropdownMenuItem onSelect={reextract} data-testid="more-reextract"><RotateCcw className="mr-2 h-4 w-4" />Neu einlesen</DropdownMenuItem>}
              {st.status === "ready" && <DropdownMenuItem onSelect={() => setReset(true)} data-testid="more-reset"><Eraser className="mr-2 h-4 w-4" />Auszug zurücksetzen</DropdownMenuItem>}
              {!st.virtual && <DropdownMenuItem onSelect={() => setDel(true)} className="text-red-700" data-testid="more-delete"><Trash2 className="mr-2 h-4 w-4" />Auszug löschen</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
          {!st.virtual && <DeleteStatement st={st} onDeleted={onDeleted} open={del} setOpen={setDel} />}
        </div>
        <ResetDialog open={reset} onOpenChange={setReset} testPrefix="statement"
          title={`${st.virtual ? st.statement_number : `Auszug ${st.statement_number || ""}`} zurücksetzen?`}
          confirmWord={st.statement_number || ""}
          run={(dry, confirm) => api.resetStatement(st.id, dry, confirm)} onDone={onChanged} />
      </div>
      {!st.virtual && st.status === "ready" && <MergeReview st={st} onChanged={onChanged} />}
      {st.status === "processing" && <Processing st={st} />}
      {st.status === "failed" && (
        <div data-testid="extraction-error" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {st.status_message}
        </div>
      )}
      {st.status === "duplicate" && (
        <div data-testid="duplicate-warning" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="flex items-center gap-2 font-semibold"><Copy className="h-4 w-4" /> Duplikat erkannt</div>
          <p className="mt-1">{st.status_message}</p>
          <Button variant="outline" size="sm" className="mt-3 bg-white" data-testid="goto-original-btn" onClick={() => onSelect(st.duplicate_of)}>Zum vorhandenen Auszug</Button>
        </div>
      )}
      {st.status === "ready" && (
        <>
          <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
            <Meta label="IBAN" value={fmtIban(st.iban)} testId="meta-iban" />
            <Meta label="Kontoinhaber" value={st.account_holder} testId="meta-holder" />
            <Meta label="Zeitraum" value={st.period_from ? `${fmtDate(st.period_from)} – ${fmtDate(st.period_to)}` : null} testId="meta-period" />
            <Meta label="Seiten / Buchungen" value={st.virtual ? `– / ${st.balance_check?.transaction_count ?? 0}` : `${st.page_count} / ${st.balance_check?.transaction_count ?? 0}`} testId="meta-counts" />
          </div>
          <div className="grid gap-4 min-[1180px]:grid-cols-[1.3fr_1fr] min-[1440px]:grid-cols-1 min-[1800px]:grid-cols-[1.3fr_1fr]">
            {st.virtual ? <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="virtual-info">Buchungen aus der Bankverbindung. Sobald der Kontoauszug als PDF hochgeladen wird, werden sie zusammengeführt – die Saldenprüfung erfolgt am PDF.</div>
              : <BalanceCheck st={st} onSave={(v) => act(() => api.patchStatement(st.id, v), "Salden gespeichert")} />}
            <div className="grid min-w-0 content-start gap-4">
              <AnalysisProgress st={st} onRerunAll={() => act(() => api.analyzeStatement(st.id, true), "KI-Analyse gestartet")} />
              <div className="flex flex-wrap gap-2">
                <AddTransactionDialog statementId={st.id} onAdded={onAdded} />
                <Button variant="outline" className="h-auto min-h-10 gap-1.5 whitespace-normal text-left" onClick={() => setBulk(true)} data-testid="bulk-no-receipt-btn"
                  title="KI-Vorschläge „Kein Beleg nötig“ übernehmen">
                  <Ban className="h-4 w-4 shrink-0" /> „Kein Beleg nötig“ übernehmen
                </Button>
              </div>
            </div>
          </div>
          <PrintButtons scope={{ statement_id: st.id }} label={`Auszug ${st.statement_number || ""}`} testPrefix="statement" />
          <ReviewDialog open={bulk} onOpenChange={setBulk} testId="no-receipt-review-dialog"
            title="„Kein Beleg nötig“ übernehmen"
            description="Diese offenen Buchungen brauchen laut KI keinen Beleg. Prüfen und abwählen, was nicht passt."
            load={async () => (await api.noReceiptSuggestions(st.id)).map((t) => ({
              key: t.id, title: `${fmtDate(t.value_date)} · ${t.ai?.merchant_clean || t.counterparty_raw} · ${fmtNum(t.amount)} €`,
              subtitle: t.ai?.category, note: t.ai?.receipt_needed_reason,
            }))}
            applyLabel="Übernehmen"
            onApply={async (items) => {
              await api.bulkStatus(items.map((i) => i.key), "kein_beleg_noetig");
              toast.success(`${items.length} Buchungen auf „Kein Beleg nötig“ gesetzt`);
              onChanged();
            }} />
          {st.extraction_warnings?.length > 0 && (
            <div data-testid="extraction-warnings" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="mb-1 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Hinweise zur Extraktion</div>
              <ul className="list-disc pl-5">{st.extraction_warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};
